import React, { useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "../../../hooks/hooks";
import {
  closeMultiPanelOffcanvas,
  setMultiPanelActivePanel,
  PanelType,
} from "../multiPanelOffcanvasSlice";
import Icon from "../../../components/Icon/Icon";

// =============================================================================
// 1. PROPS INTERFACES (for main component and subcomponents)
// =============================================================================

interface MultiPanelOffcanvasProps {
  searchPanel: React.ReactNode;
  cartPanel: React.ReactNode;
  wishlistPanel: React.ReactNode;
}

interface TabHeaderProps {
  activePanel: PanelType | null;
  onTabClick: (panel: PanelType) => void;
  onClose: () => void;
}

interface TabContentProps {
  activePanel: PanelType | null;
  searchPanel: React.ReactNode;
  cartPanel: React.ReactNode;
  wishlistPanel: React.ReactNode;
}

// =============================================================================
// 2. SUBCOMPONENTS (for better structure)
// =============================================================================

/**
 * TabHeader: A pure UI component responsible only for rendering the tabs and close button.
 */
const TabHeader: React.FC<TabHeaderProps> = ({
  activePanel,
  onTabClick,
  onClose,
}) => {
  const { t } = useTranslation();
  return (
    <div className="offcanvas-header multipanel-offcanvas__header">
      <div className="multipanel-offcanvas__tabs">
        <button
          className={`multipanel-offcanvas__tab ${
            activePanel === "search" ? "is-active" : ""
          }`}
          onClick={() => onTabClick("search")}
          aria-controls="search-panel"
          aria-selected={activePanel === "search"}
          role="tab"
        >
          <Icon iconName="search" className="icon--stroke-thick" />
          <span>{t("searchOffcanvas.title", "Search")}</span>
        </button>
        <button
          className={`multipanel-offcanvas__tab ${
            activePanel === "cart" ? "is-active" : ""
          }`}
          onClick={() => onTabClick("cart")}
          aria-controls="cart-panel"
          aria-selected={activePanel === "cart"}
          role="tab"
        >
          <Icon iconName="shopping-cart-filled" className="icon--filled" />
          <span>{t("cartOffcanvas.title", "Shopping Cart")}</span>
        </button>
        <button
          className={`multipanel-offcanvas__tab ${
            activePanel === "wishlist" ? "is-active" : ""
          }`}
          onClick={() => onTabClick("wishlist")}
          aria-controls="wishlist-panel"
          aria-selected={activePanel === "wishlist"}
          role="tab"
        >
          <Icon iconName="heart-filled" className="icon--filled" />
          <span>{t("wishlistOffcanvas.panelTitle", "Wishlist")}</span>
        </button>
      </div>
      <button
        onClick={onClose}
        className="offcanvas-close-btn multipanel-offcanvas__close-btn"
        aria-label="Close"
      >
        <Icon iconName="x-mark" className="icon--stroke-thickest" />
      </button>
    </div>
  );
};

/**
 * TabContent: A pure UI component responsible only for rendering the correct panel content.
 */
const TabContent: React.FC<TabContentProps> = ({
  activePanel,
  cartPanel,
  searchPanel,
  wishlistPanel,
}) => (
  <div className="offcanvas-body multipanel-offcanvas__body">
    <div
      id="search-panel"
      role="tabpanel"
      hidden={activePanel !== "search"}
      className="multipanel-offcanvas__panel-content"
    >
      {activePanel === "search" && searchPanel}
    </div>
    <div
      id="cart-panel"
      role="tabpanel"
      hidden={activePanel !== "cart"}
      className="multipanel-offcanvas__panel-content"
    >
      {activePanel === "cart" && cartPanel}
    </div>
    <div
      id="wishlist-panel"
      role="tabpanel"
      hidden={activePanel !== "wishlist"}
      className="multipanel-offcanvas__panel-content"
    >
      {activePanel === "wishlist" && wishlistPanel}
    </div>
  </div>
);

// =============================================================================
// 3. MAIN COMPONENT
// =============================================================================

const MultiPanelOffcanvas: React.FC<MultiPanelOffcanvasProps> = (props) => {
  const dispatch = useAppDispatch();
  const { isOpen, activePanel } = useAppSelector(
    (state) => state.multiPanelOffcanvas
  );
  const panelRef = useRef<HTMLDivElement>(null);
  const portalRoot = document.body;

  // --- State management and effects ---
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        dispatch(closeMultiPanelOffcanvas());
      }
    };

    if (isOpen) {
      document.body.classList.add("multipanel-offcanvas-open");
      document.addEventListener("keydown", handleKeyDown);
      setTimeout(() => panelRef.current?.focus(), 150);
    } else {
      document.body.classList.remove("multipanel-offcanvas-open");
    }

    return () => {
      document.body.classList.remove("multipanel-offcanvas-open");
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, dispatch]);

  const handlePanelSwitch = (panel: PanelType) => {
    dispatch(setMultiPanelActivePanel(panel));
  };

  const handleClose = () => {
    dispatch(closeMultiPanelOffcanvas());
  };

  if (!portalRoot) {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      className={`offcanvas-wrapper multipanel-offcanvas-wrapper ${
        isOpen ? "is-open" : ""
      }`}
      aria-hidden={!isOpen}
    >
      <div
        className="offcanvas-overlay multipanel-offcanvas__overlay"
        onClick={handleClose}
      />
      <div
        ref={panelRef}
        className="offcanvas-panel offcanvas-panel--from-right multipanel-offcanvas__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="multipanel-offcanvas-title"
        tabIndex={-1}
      >
        <TabHeader
          activePanel={activePanel}
          onTabClick={handlePanelSwitch}
          onClose={handleClose}
        />
        <TabContent activePanel={activePanel} {...props} />
      </div>
    </div>,
    portalRoot
  );
};

export default MultiPanelOffcanvas;
